import React, {useState} from "react";
import {
    Box, Typography, Paper, CircularProgress, Chip, Accordion,
    AccordionSummary, AccordionDetails,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {accountingApi} from "../../services/api/accounting";
import {showSnackbar} from "../../components/SnackBar/SnackBar";
import ReportFilters from "./ReportFilters";

const TYPE_COLORS = {
    ASSET: "#1565c0", LIABILITY: "#c62828", EQUITY: "#2e7d32",
    REVENUE: "#00838f", EXPENSE: "#e65100",
};

const formatMoney = (amount) => {
    if (amount === null || amount === undefined) return "KES 0.00";
    const num = Number(amount);
    const formatted = Math.abs(num).toLocaleString("en-KE", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    return num < 0 ? `(KES ${formatted})` : `KES ${formatted}`;
};

export default function GeneralLedger() {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [downloading, setDownloading] = useState(false);

    const handleApply = async (params) => {
        setLoading(true);
        try {
            const response = await accountingApi.getGeneralLedger(params);
            if (response.ok && response.data) {
                setData(response.data);
            } else {
                setData(null);
            }
        } catch (err) {
            console.error("Failed to fetch general ledger:", err);
        } finally {
            setLoading(false);
        }
    };

    const handlePDF = async (params) => {
        setDownloading(true);
        try {
            const response = await accountingApi.downloadGeneralLedgerPDF(params);
            if (response.ok && response.blob) {
                const url = window.URL.createObjectURL(response.blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `General_Ledger_${params.start_date}_to_${params.end_date}.pdf`;
                a.click();
                window.URL.revokeObjectURL(url);
            } else {
                showSnackbar({title: "Error!", message: "Failed to download PDF.", type: "error", position: {vertical: "bottom", horizontal: "right"}});
            }
        } catch (err) {
            showSnackbar({title: "Error!", message: "Failed to download PDF.", type: "error", position: {vertical: "bottom", horizontal: "right"}});
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box sx={{py: 3}}>
            <Typography variant="h5" fontWeight={600} sx={{mb: 3, color: "#2C5283"}}>
                General Ledger
            </Typography>
            <ReportFilters onApply={handleApply} onDownloadPDF={handlePDF} downloading={downloading}/>

            {loading ? (
                <Box sx={{display: "flex", justifyContent: "center", py: 6}}><CircularProgress/></Box>
            ) : !data ? (
                <Paper sx={{py: 6, textAlign: "center", borderRadius: 2}}>
                    <Typography color="textSecondary">Select a date range and click Apply to generate the report.</Typography>
                </Paper>
            ) : data.accounts?.length === 0 ? (
                <Paper sx={{py: 6, textAlign: "center", borderRadius: 2}}>
                    <Typography color="textSecondary">No account activity in this period.</Typography>
                </Paper>
            ) : (
                <Box>
                    {data.period && (
                        <Paper sx={{px: 3, py: 1.5, mb: 2, borderRadius: 2, bgcolor: "#f5f7fa"}}>
                            <Typography variant="body2" color="textSecondary">
                                Period: {data.period.start} to {data.period.end}
                            </Typography>
                        </Paper>
                    )}
                    {data.accounts?.map((account) => (
                        <Accordion key={account.code} defaultExpanded={false} sx={{mb: 1, borderRadius: "8px !important", "&:before": {display: "none"}}}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon/>} sx={{bgcolor: "#f5f7fa"}}>
                                <Box sx={{display: "flex", alignItems: "center", gap: 1.5, width: "100%"}}>
                                    <Typography variant="body2" sx={{fontFamily: "monospace", fontWeight: 600}}>{account.code}</Typography>
                                    <Typography variant="body1" fontWeight={500}>{account.name}</Typography>
                                    <Chip label={account.account_type} size="small"
                                        sx={{bgcolor: TYPE_COLORS[account.account_type] || "#616161", color: "#fff", fontSize: "10px", fontWeight: 600, ml: "auto", mr: 2}}/>
                                    <Typography variant="body2" fontWeight={600} sx={{minWidth: 120, textAlign: "right"}}>
                                        Bal: {formatMoney(account.closing_balance)}
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{p: 0}}>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{bgcolor: "#fafafa"}}>
                                                <TableCell sx={{fontWeight: 600, width: 120}}>Date</TableCell>
                                                <TableCell sx={{fontWeight: 600}}>Description</TableCell>
                                                <TableCell align="right" sx={{fontWeight: 600, color: "#1565c0", width: 130}}>Debit</TableCell>
                                                <TableCell align="right" sx={{fontWeight: 600, color: "#2e7d32", width: 130}}>Credit</TableCell>
                                                <TableCell align="right" sx={{fontWeight: 600, width: 140}}>Balance</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {/* Opening Balance */}
                                            <TableRow sx={{bgcolor: "#f0f7ff"}}>
                                                <TableCell colSpan={4} sx={{fontWeight: 600, fontStyle: "italic"}}>Opening Balance</TableCell>
                                                <TableCell align="right" sx={{fontWeight: 600}}>{formatMoney(account.opening_balance)}</TableCell>
                                            </TableRow>
                                            {account.entries?.map((entry, i) => (
                                                <TableRow key={i} sx={{"&:hover": {bgcolor: "#fafafa"}}}>
                                                    <TableCell>{new Date(entry.entry_date).toLocaleDateString("en-KE", {month: "short", day: "numeric"})}</TableCell>
                                                    <TableCell>{entry.description}</TableCell>
                                                    <TableCell align="right" sx={{color: "#1565c0"}}>{entry.debit > 0 ? formatMoney(entry.debit) : "-"}</TableCell>
                                                    <TableCell align="right" sx={{color: "#2e7d32"}}>{entry.credit > 0 ? formatMoney(entry.credit) : "-"}</TableCell>
                                                    <TableCell align="right" sx={{fontWeight: 500}}>{formatMoney(entry.balance)}</TableCell>
                                                </TableRow>
                                            ))}
                                            {/* Closing Balance */}
                                            <TableRow sx={{bgcolor: "#f0f7ff"}}>
                                                <TableCell colSpan={4} sx={{fontWeight: 600, fontStyle: "italic"}}>Closing Balance</TableCell>
                                                <TableCell align="right" sx={{fontWeight: 700}}>{formatMoney(account.closing_balance)}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            )}
        </Box>
    );
}
